/**
 * The relay graph (M3 spec §3): regional MSTs, 1–3 chokepoint links per neighbouring region pair, extra
 * local links up to the target degree, 2–4 long offline shortcuts, then online states by region.
 * Works on the 2D (x, y) projection of the map. 48 nodes, so the O(n²) / O(E²) loops below are fine.
 */
import { logger } from '../core/logger'
import { createRng, nextFloat, type Rng } from '../core/rng'
import type { GalaxyConfig } from '../data/galaxyConfig'
import { homeIndex, systemId, type PlacedSystem } from './placement'
import { deriveSeed, intRange } from './seed'
import type { Relay } from './types'

export type Vec2 = readonly [number, number]

/** Logger message emitted when fewer long relays than rolled could be placed. */
export const LONG_RELAYS_SHORT = 'galaxy: fewer long relays than rolled'
/**
 * Warning (with `{ region, arcs }`) when a region splits into more arcs than the chokepoint budget allows, so it
 * gets one link per arc instead (a 4-arc Rim in about 1 of 10,000 seeds). Connectivity wins over the budget.
 */
export const CHOKEPOINTS_OVER_BUDGET = 'galaxy: chokepoint budget exceeded to join a split region'
/** Debug message (with `{ region, parts }`) when a region's own MST falls into several arcs. */
export const REGION_SPLIT = 'galaxy: region MST is split'

type EdgeKind = 'intra' | 'cross' | 'long'

interface Edge {
  a: number
  b: number
  kind: EdgeKind
}

function orient(a: Vec2, b: Vec2, c: Vec2): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
}

/** True only for a proper crossing: shared endpoints, T-touches and collinear overlaps are not crossings. */
export function segmentsIntersect(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean {
  const d1 = orient(p3, p4, p1)
  const d2 = orient(p3, p4, p2)
  const d3 = orient(p1, p2, p3)
  const d4 = orient(p1, p2, p4)
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
}

/** Distance from point p to the segment a–b (to its nearest point, endpoints included). */
export function pointSegmentDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)) : 0
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy))
}

/** Hop counts from `from` over undirected edges; -1 where unreachable. */
export function bfsHops(nodeCount: number, edges: readonly (readonly [number, number])[], from: number): number[] {
  const adj: number[][] = Array.from({ length: nodeCount }, () => [])
  for (const [a, b] of edges) {
    adj[a].push(b)
    adj[b].push(a)
  }
  const hops = new Array<number>(nodeCount).fill(-1)
  hops[from] = 0
  const queue = [from]
  for (let head = 0; head < queue.length; head++) {
    const n = queue[head]
    for (const m of adj[n]) {
      if (hops[m] !== -1) continue
      hops[m] = hops[n] + 1
      queue.push(m)
    }
  }
  return hops
}

class Graph {
  readonly edges: Edge[] = []
  readonly deg: number[]
  private readonly keys = new Set<number>()
  readonly pos: readonly Vec2[]
  readonly n: number
  readonly cap: number
  readonly clearance: number

  constructor(pos: readonly Vec2[], n: number, cap: number, clearance: number) {
    this.pos = pos
    this.n = n
    this.cap = cap
    this.clearance = clearance
    this.deg = new Array<number>(n).fill(0)
  }

  has(a: number, b: number): boolean {
    return this.keys.has(a < b ? a * this.n + b : b * this.n + a)
  }

  add(a: number, b: number, kind: EdgeKind): void {
    this.edges.push({ a, b, kind })
    this.keys.add(a < b ? a * this.n + b : b * this.n + a)
    this.deg[a]++
    this.deg[b]++
  }

  /** ponytail: linear scan of every edge per candidate; O(E) × O(n²) candidates stays well under a millisecond. */
  crosses(a: number, b: number): boolean {
    const pa = this.pos[a]
    const pb = this.pos[b]
    for (const e of this.edges) {
      if (e.a === a || e.a === b || e.b === a || e.b === b) continue
      if (segmentsIntersect(pa, pb, this.pos[e.a], this.pos[e.b])) return true
    }
    return false
  }

  /** True when the segment a–b passes closer than the clearance to any system other than a and b. ponytail: O(n). */
  tooClose(a: number, b: number): boolean {
    for (let c = 0; c < this.n; c++) {
      if (c !== a && c !== b && pointSegmentDistance(this.pos[c], this.pos[a], this.pos[b]) < this.clearance) return true
    }
    return false
  }

  /** A new relay a–b is blocked if it would cross an existing relay or pass too close to another system. */
  blocked(a: number, b: number): boolean {
    return this.tooClose(a, b) || this.crosses(a, b)
  }

  pairs(): [number, number][] {
    return this.edges.map((e) => [e.a, e.b])
  }
}

function dist(p: Vec2, q: Vec2): number {
  return Math.hypot(p[0] - q[0], p[1] - q[1])
}

/** Candidate pairs sorted shortest first (stable, so ties keep index order). */
function sortedPairs(pos: readonly Vec2[], pairs: [number, number][]): [number, number][] {
  return pairs
    .map((p) => ({ p, d: dist(pos[p[0]], pos[p[1]]) }))
    .sort((x, y) => x.d - y.d)
    .map((x) => x.p)
}

function find(parent: number[], i: number): number {
  let r = i
  while (parent[r] !== r) r = parent[r]
  let c = i
  while (parent[c] !== r) {
    const next = parent[c]
    parent[c] = r
    c = next
  }
  return r
}

export function buildRelays(seed: number, placed: readonly PlacedSystem[], cfg: GalaxyConfig): Relay[] {
  const n = placed.length
  const pos: Vec2[] = placed.map((p) => [p.mapPos[0], p.mapPos[1]])
  const regionOf = placed.map((p) => cfg.regions.findIndex((r) => r.id === p.region))
  const members = cfg.regions.map((_, r) => regionOf.flatMap((ri, i) => (ri === r ? [i] : [])))
  const rc = cfg.relays
  const cap = rc.maxDegree
  const g = new Graph(pos, n, rc.maxDegree, rc.clearance)
  const rng = createRng(deriveSeed(seed, 'relays'))

  // 1 + 2, interleaved inside out: each region's MST, then the chokepoints joining it to the region inside it.
  // Linking a region before building the next one out keeps an outer chord from walling off an inner arc.
  const parent = Array.from({ length: n }, (_, i) => i)
  members.forEach((nodes, r) => {
    regionMst(g, nodes, parent, cfg.regions[r].id)
    if (r > 0) chokepoints(g, rng, members, r, cfg)
  })

  // 3. Extra local links, shortest first, until the average degree reaches the target less the slack.
  const needEdges = Math.ceil(((rc.targetAvgDegree - rc.degreeSlack) * n) / 2 - 1e-9)
  const intra = sortedPairs(pos, members.flatMap((nodes) => allPairs(nodes, nodes)))
  for (const [a, b] of intra) {
    if (g.edges.length >= needEdges) break
    if (g.has(a, b) || g.deg[a] >= cap || g.deg[b] >= cap || g.blocked(a, b)) continue
    g.add(a, b, 'intra')
  }

  // 4. Long relays: shortcuts between systems ≥ longMinHops apart, one per system, non-crossing where possible;
  //    each takes the widest remaining hop gap, which cuts the longest routes (D-037).
  addLongRelays(g, rng, cfg)

  // 5. Online states, grown outward from home to the regional reach targets.
  return assignOnline(seed, g, regionOf, homeIndex(placed), cfg)
}

/**
 * Step 1: the region's MST (Kruskal on Euclidean length), skipping links that would cross an accepted link
 * or push a node past the degree cap. A sparse ring (usually the Rim) can end up as several arcs when its
 * chords would cut across inner-region links; the chokepoint step joins every arc.
 */
function regionMst(g: Graph, nodes: readonly number[], parent: number[], region: string): void {
  const cap = g.cap
  for (const [a, b] of sortedPairs(g.pos, allPairs(nodes, nodes))) {
    const ra = find(parent, a)
    const rb = find(parent, b)
    if (ra === rb || g.deg[a] >= cap || g.deg[b] >= cap || g.blocked(a, b)) continue
    g.add(a, b, 'intra')
    parent[ra] = rb
  }
  const roots = new Set(nodes.map((i) => find(parent, i)))
  if (roots.size > 1) logger.debug(REGION_SPLIT, { region, parts: roots.size })
}

/**
 * Step 2: k non-crossing links between region r − 1 and region r (the chokepoints), spread around the ring. First
 * every arc of region r that isn't yet joined to the core gets its own shortest link inward (k is raised to the arc
 * count if needed), so a split region still ends up connected. The ring is then cut into k equal sectors (rotated by
 * a seeded offset), and each sector without a chokepoint gets its shortest valid link; if a sector has none, the
 * shortest valid link at least half a sector away from the others stands in. Spreading them shortens routes (D-037).
 */
function chokepoints(g: Graph, rng: Rng, members: readonly (readonly number[])[], r: number, cfg: GalaxyConfig): void {
  const rc = cfg.relays
  const cap = g.cap
  const k = intRange(rng, rc.crossLinks[0], rc.crossLinks[1])
  const pairs = sortedPairs(g.pos, allPairs(members[r - 1], members[r]))
  const valid = (a: number, b: number, limit: number): boolean =>
    !g.has(a, b) && g.deg[a] < limit && g.deg[b] < limit && !g.blocked(a, b)
  const comp = componentLabels(g.n, g.pairs())
  const core = comp[members[0][0]]
  const arcs = [...new Set(members[r].map((i) => comp[i]))].filter((c) => c !== core)
  const mine: [number, number][] = []
  const link = (a: number, b: number): void => {
    g.add(a, b, 'cross')
    mine.push([a, b])
  }
  for (const arc of arcs) {
    const joins = ([a, b]: [number, number]): boolean => comp[a] === core && comp[b] === arc
    // Degree cap first; if the arc can only be reached through a full node, allow one over (max degree ≤ cap + 1).
    const pick = pairs.find((p) => joins(p) && valid(p[0], p[1], cap)) ?? pairs.find((p) => joins(p) && valid(p[0], p[1], cap + 1))
    if (!pick) {
      logger.error('galaxy: no chokepoint link can reach a split region', { region: cfg.regions[r].id })
      continue
    }
    link(pick[0], pick[1])
  }
  if (mine.length > rc.crossLinks[1]) logger.warn(CHOKEPOINTS_OVER_BUDGET, { region: cfg.regions[r].id, arcs: arcs.length })

  const offset = nextFloat(rng) * TAU // always drawn, so k = 1 keeps the same sequence
  const width = TAU / k
  const angleOf = ([a, b]: readonly [number, number]): number =>
    Math.atan2((g.pos[a][1] + g.pos[b][1]) / 2, (g.pos[a][0] + g.pos[b][0]) / 2)
  const sectorOf = (p: readonly [number, number]): number => Math.min(k - 1, Math.floor((((angleOf(p) - offset) % TAU) + TAU) % TAU / width))
  const used = new Set(mine.map(sectorOf))
  for (let sector = 0; sector < k && mine.length < k; sector++) {
    if (used.has(sector)) continue
    const pick = pairs.find((p) => sectorOf(p) === sector && valid(p[0], p[1], cap))
    if (!pick) continue
    link(pick[0], pick[1])
    used.add(sector)
  }
  for (const p of pairs) {
    if (mine.length >= k) break
    if (!valid(p[0], p[1], cap) || mine.some((q) => angleGap(angleOf(p), angleOf(q)) < width / 2)) continue
    link(p[0], p[1])
  }
}

const TAU = Math.PI * 2

/** The unsigned angle between two directions, in [0, π]. */
function angleGap(x: number, y: number): number {
  const d = Math.abs(x - y) % TAU
  return d > Math.PI ? TAU - d : d
}

/** Connected-component label per node (the root index of its union-find set). */
function componentLabels(n: number, edges: readonly (readonly [number, number])[]): number[] {
  const parent = Array.from({ length: n }, (_, i) => i)
  for (const [a, b] of edges) parent[find(parent, a)] = find(parent, b)
  return parent.map((_, i) => find(parent, i))
}

function allPairs(as: readonly number[], bs: readonly number[]): [number, number][] {
  const out: [number, number][] = []
  const same = as === bs
  for (let i = 0; i < as.length; i++) {
    for (let j = same ? i + 1 : 0; j < bs.length; j++) out.push([as[i], bs[j]])
  }
  return out
}

function addLongRelays(g: Graph, rng: Rng, cfg: GalaxyConfig): void {
  const rc = cfg.relays
  const count = intRange(rng, rc.longRelays[0], rc.longRelays[1])
  const hasLong = new Array<boolean>(g.n).fill(false)
  let added = 0
  for (; added < count; added++) {
    // ponytail: all-pairs BFS per long relay (48 BFS runs × ≤ 4) instead of an incremental update
    const hops = Array.from({ length: g.n }, (_, i) => bfsHops(g.n, g.pairs(), i))
    const clean: [number, number][] = []
    const crossing: [number, number][] = []
    for (let a = 0; a < g.n; a++) {
      for (let b = a + 1; b < g.n; b++) {
        if (hasLong[a] || hasLong[b] || g.has(a, b) || g.deg[a] > cfg.relays.maxDegree || g.deg[b] > cfg.relays.maxDegree) continue
        const h = hops[a][b]
        if (h !== -1 && h < rc.longMinHops) continue
        if (dist(g.pos[a], g.pos[b]) > rc.longMaxLength || g.tooClose(a, b)) continue // clearance is never waived
        ;(g.crosses(a, b) ? crossing : clean).push([a, b])
      }
    }
    const candidates = clean.length > 0 ? clean : crossing
    if (candidates.length === 0) break
    // The biggest shortcut: the pair furthest apart in hops (unreachable counts as infinitely far), seeded ties.
    const gap = ([x, y]: [number, number]): number => (hops[x][y] < 0 ? Infinity : hops[x][y])
    const widest = Math.max(...candidates.map(gap))
    const pool = candidates.filter((c) => gap(c) === widest)
    const [a, b] = pool[Math.floor(nextFloat(rng) * pool.length)]
    g.add(a, b, 'long')
    hasLong[a] = true
    hasLong[b] = true
  }
  if (added < rc.longRelays[0]) logger.info(LONG_RELAYS_SHORT, { rolled: count, placed: added })
}

/**
 * Step 5, online states from reachability targets (D-037). Gate links and Gate–Mid chokepoints are always online.
 * Then an online tree grows outward from home, region by region, opening random frontier relays until the region's
 * `onlineReach` share of systems is reachable; a region with no frontier yet is reached by opening the fewest relays
 * needed. Finally, relays between two reached systems form loops (`onlineLoopChance`), relays between two unreached
 * systems form islands (`onlineIslandChance`), and every relay from a reached to an unreached system stays closed.
 * Long relays and anything touching the Rim stay offline.
 */
function assignOnline(seed: number, g: Graph, regionOf: readonly number[], home: number, cfg: GalaxyConfig): Relay[] {
  const rng = createRng(deriveSeed(seed, 'online'))
  const last = cfg.regions.length - 1
  const ends = g.pairs()
  const usable = (i: number): boolean => g.edges[i].kind !== 'long' && regionOf[ends[i][0]] !== last && regionOf[ends[i][1]] !== last
  const online = ends.map(([a, b], i) => usable(i) && Math.min(regionOf[a], regionOf[b]) === 0 && Math.max(regionOf[a], regionOf[b]) <= 1)
  const reached = bfsHops(
    g.n,
    ends.filter((_, i) => online[i]),
    home,
  ).map((h) => h >= 0)

  for (let r = 1; r < last; r++) {
    const band = cfg.regions[r]
    const members = regionOf.flatMap((ri, i) => (ri === r ? [i] : []))
    const target = Math.round(band.onlineReach * members.length)
    const within = (i: number): boolean => usable(i) && regionOf[ends[i][0]] <= r && regionOf[ends[i][1]] <= r
    const newIn = (v: number): boolean => !reached[v] && regionOf[v] === r
    let count = members.filter((i) => reached[i]).length
    while (count < target) {
      const frontier = ends.flatMap(([a, b], i) => (within(i) && ((reached[a] && newIn(b)) || (reached[b] && newIn(a))) ? [i] : []))
      const opened =
        frontier.length > 0
          ? [frontier[Math.floor(nextFloat(rng) * frontier.length)]]
          : cheapestOpening(
              g.n,
              ends,
              online,
              within,
              reached.flatMap((x, i) => (x ? [i] : [])),
              newIn,
            )
      if (!opened) break
      for (const i of opened) {
        online[i] = true
        for (const v of ends[i]) {
          if (reached[v]) continue
          reached[v] = true
          if (regionOf[v] === r) count++
        }
      }
    }
    if (count < target) logger.warn('galaxy: online reach fell short of its target', { region: band.id, count, target })
  }

  const rc = cfg.relays
  const final = ends.map(([a, b], i) => {
    const roll = nextFloat(rng) // always drawn, so a rule change doesn't reshuffle every later relay
    if (online[i]) return true
    if (!usable(i) || reached[a] !== reached[b]) return false
    return roll < (reached[a] ? rc.onlineLoopChance : rc.onlineIslandChance)
  })
  return g.edges.map((e, i) => ({
    id: `relay-${String(i).padStart(3, '0')}`,
    a: systemId(e.a),
    b: systemId(e.b),
    online: final[i],
    long: e.kind === 'long',
    crossRegion: regionOf[e.a] !== regionOf[e.b],
  }))
}

/**
 * The cheapest way to reach a target from any source when open relays cost 0 and closed ones cost 1, walking only
 * `usable` relays. Returns the relay indices along the path, or null. ponytail: O(n²) Dijkstra, n is about 48.
 */
function cheapestOpening(
  n: number,
  ends: readonly (readonly [number, number])[],
  online: readonly boolean[],
  usable: (i: number) => boolean,
  sources: readonly number[],
  isTarget: (v: number) => boolean,
): number[] | null {
  const adj: [number, number][][] = Array.from({ length: n }, () => [])
  ends.forEach(([a, b], i) => {
    if (!usable(i)) return
    adj[a].push([b, i])
    adj[b].push([a, i])
  })
  const cost = new Array<number>(n).fill(Infinity)
  const via = new Array<number>(n).fill(-1)
  const done = new Array<boolean>(n).fill(false)
  for (const src of sources) cost[src] = 0
  for (;;) {
    let u = -1
    for (let i = 0; i < n; i++) if (!done[i] && cost[i] < Infinity && (u === -1 || cost[i] < cost[u])) u = i
    if (u === -1) return null
    if (isTarget(u)) {
      const path: number[] = []
      for (let v = u; via[v] !== -1; ) {
        const i = via[v]
        path.push(i)
        v = ends[i][0] === v ? ends[i][1] : ends[i][0]
      }
      return path
    }
    done[u] = true
    for (const [v, i] of adj[u]) {
      const c = cost[u] + (online[i] ? 0 : 1)
      if (c < cost[v]) {
        cost[v] = c
        via[v] = i
      }
    }
  }
}

/** Info message (with `{ to, opened }`) when openRoute had to bring relays online. */
export const ROUTE_OPENED = 'galaxy: opened relays to make a system reachable online'

/**
 * Brings the fewest offline relays online so `to` is reachable from `from` over online relays (used for anchors that
 * must be reachable at the start, such as the megacorp HQ). Never opens long relays or anything touching the Rim;
 * logs an error and changes nothing if no such route exists.
 */
export function openRoute(placed: readonly PlacedSystem[], relays: readonly Relay[], from: number, to: number, cfg: GalaxyConfig): Relay[] {
  const last = cfg.regions[cfg.regions.length - 1].id
  const index = new Map(placed.map((_, i) => [systemId(i), i]))
  const ends = relays.map((r): [number, number] => [index.get(r.a) ?? -1, index.get(r.b) ?? -1])
  const usable = (i: number): boolean => !relays[i].long && ends[i].every((v) => v >= 0 && placed[v].region !== last)
  const path =
    placed[to].region === last
      ? null
      : cheapestOpening(
          placed.length,
          ends,
          relays.map((r) => r.online),
          usable,
          [from],
          (v) => v === to,
        )
  if (!path) {
    logger.error('galaxy: no route can be opened', { from: systemId(from), to: systemId(to) })
    return [...relays]
  }
  const open = new Set(path.filter((i) => !relays[i].online))
  if (open.size > 0) logger.info(ROUTE_OPENED, { to: systemId(to), opened: open.size })
  return relays.map((r, i) => (open.has(i) ? { ...r, online: true } : r))
}
